import { WorkbenchTaxonomy } from "../../../common/api/v1.1/types/taxonomy";
import { ConfidenceClass } from "./Document/getConfidenceClass";
import { IFontInformation } from "./Document/InnodomContent/types";

// taxonomy types
export enum TaxonomyEntryType {
  Boolean = "boolean",
  Date = "date",
  DateTime = "datetime",
  Enum = "enum",
  Group = "group",
  Number = "number",
  Pointer = "pointer",
  Selection = "selection",
  String = "string",
  Time = "time",
}
export type BaseTaxonomyEntry<T extends TaxonomyEntryType> = {
  id: string;
  children?: TaxonomyEntry[];
  description?: string;
  groups: Array<string>;
  key: string;
  multiple: boolean;
  question: string;
  required: boolean;
  type: T;
  prompt?: string;
};

export type BooleanTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Boolean>;
export type DateTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Date> &
  Readonly<{
    min?: Date;
    max?: Date;
  }>;
export type DateTimeTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.DateTime> &
  Readonly<{
    min?: Date;
    max?: Date;
  }>;
export type EnumTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Enum> &
  Readonly<{
    options: Array<string>;
  }>;
export type NumberTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Number> &
  Readonly<{
    min?: number;
    max?: number;
  }>;
export type PointerTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Pointer>;
export type SelectionTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Selection> &
  Readonly<{
    maxLength?: number;
    minLength?: number;
  }>;
export type StringTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.String> &
  Readonly<{
    maxLength?: number;
    minLength?: number;
  }>;
export type TimeTaxonomyEntry = BaseTaxonomyEntry<TaxonomyEntryType.Time>;
export type NonGroupTaxonomyEntry =
  | BooleanTaxonomyEntry
  | DateTaxonomyEntry
  | DateTimeTaxonomyEntry
  | EnumTaxonomyEntry
  | NumberTaxonomyEntry
  | PointerTaxonomyEntry
  | SelectionTaxonomyEntry
  | StringTaxonomyEntry
  | TimeTaxonomyEntry;
export type GroupTaxonomyEntry = Omit<BaseTaxonomyEntry<TaxonomyEntryType.Group>, "groups"> &
  Readonly<{
    dataPoints?: Array<string>;
    children?: Array<NonGroupTaxonomyEntry>;
    description: string;
  }>;
export type TaxonomyEntry = NonGroupTaxonomyEntry | GroupTaxonomyEntry;

// content block types
export enum ContentType {
  Image,
  Math,
  Table,
  TableCell,
  Text,
}
type BaseBlock<T extends ContentType> = Readonly<{
  confidenceClass: ConfidenceClass;
  element: Element;
  height: number;
  id: string;
  page: number;
  references: Array<string>;
  type: T;
  width: number;
  x: number;
  y: number;
  highlights: Array<string>;
}>;
export type ImageBlock = BaseBlock<ContentType.Image> &
Readonly<{
  text: string;
}>;
export type MathBlock = BaseBlock<ContentType.Math> &
Readonly<{
  text: string;
}>;
export type TableCellBlock = BaseBlock<ContentType.TableCell> &
  Readonly<{
    col: number;
    colSpan: number;
    row: number;
    rowSpan: number;
    text: string;
  }>;
export type TableBlock = BaseBlock<ContentType.Table> &
  Readonly<{
    cells: Array<Array<string>>;
    text: string;
  }>;
export type TextBlock = BaseBlock<ContentType.Text> &
  {
    text: string;
  }
export type Block = ImageBlock | MathBlock | TableBlock | TableCellBlock | TextBlock;
export type PageMeta = Readonly<{
  height: number;
  width: number;
}>;
export type Page = Readonly<{
  blocks: Array<string>;
  height?: number;
  src?: string;
  width?: number;
}>;

// data point values
export type DataPointValue<T> = Readonly<{
  confidence: number;
  idRef: string | null;
  taxonomyKey: string; // Simple key for the leaf (group/point)
  value: T;
  valueKey: string; // Composite key (group.index.point.index)
  valueRaw: string; 
}>;

// full state
export type State = Readonly<{
  authToken: string;
  activeValue: string;
  blocks: {
    [blockId: string]: Block;
  };
  confidenceThreshold: number;
  fontInformation?: IFontInformation;
  modified: boolean;
  pages: Array<Page>;
  scrollToBlock: string;
  taxonomy: WorkbenchTaxonomy;
  values: {
    [valueKey: string]: DataPointValue<any>;
  };
}>;

export type Settings = Readonly<{
  automaticallyJumpToText: boolean;
  automaticallyScrollDocument: boolean;
  openaiApiKey: string;
}>;
